import { createClient } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';

// Shared Supabase client instance
let supabaseClient: any = null;
let clientInitialized = false;

// Helper to get config (for backwards compatibility with existing code)
const getSupabaseConfig = async () => {
  if (!projectId || !publicAnonKey) {
    return null;
  }
  return { projectId, publicAnonKey };
};

const getSupabaseClient = async () => {
  // Return existing client if already initialized
  if (supabaseClient && clientInitialized) {
    return supabaseClient;
  }
  
  if (!projectId || !publicAnonKey) {
    console.warn('Supabase configuration not available, running in offline mode');
    return null;
  }
  
  // Only create client if not already created
  if (!supabaseClient) {
    supabaseClient = createClient(
      `https://${projectId}.supabase.co`,
      publicAnonKey,
      {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: false,
          storage: window.localStorage,
          storageKey: 'sentlo-auth-token',
        }
      }
    );
    clientInitialized = true;
    console.log('✅ Supabase client initialized (singleton)');
  }
  
  return supabaseClient;
};

const getApiBaseUrl = async () => {
  if (!projectId) return null;
  return `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75`;
};

// Export the shared Supabase client getter to prevent multiple instances
export const getSharedSupabaseClient = getSupabaseClient;

export const authService = {
  /**
   * Sign up a new user with Supabase backend
   */
  async signUp(email: string, password: string, name: string, phone: string, country: string, state?: string) {
    const config = await getSupabaseConfig();
    const API_BASE_URL = await getApiBaseUrl();
    
    if (!config || !API_BASE_URL) {
      console.error('❌ Supabase config missing:', { config: !!config, API_BASE_URL });
      throw new Error('Backend service not available. Running in offline mode.');
    }
    
    console.log('🚀 Starting signup request to:', `${API_BASE_URL}/auth/signup`);
    
    const registrationStart = Date.now();
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${config.publicAnonKey}`,
        },
        body: JSON.stringify({
          email,
          password,
          name,
          phone,
          country,
          state: state || (country === 'Nigeria' ? 'Lagos' : undefined),
          registrationSpeed: Date.now() - registrationStart
        }),
      });

      console.log('📡 Signup response status:', response.status);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        console.error('❌ Signup failed:', errorData);
        throw new Error(errorData.error || `Signup failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Signup successful, user created:', data.user?.id);
      
      // Store session info
      if (data.user) {
        localStorage.setItem('sentlo_session', JSON.stringify({
          user: data.user,
          profile: data.profile,
          timestamp: Date.now()
        }));
      }

      return data;
    } catch (error: any) {
      console.error('❌ Signup request error:', error);
      if (error.message && error.message !== 'Signup failed') {
        throw error;
      }
      throw new Error(`Signup request failed: ${error.message || 'Unknown error'}`);
    }
  },

  /**
   * Sign in an existing user with Supabase
   */
  async signIn(email: string, password: string) {
    const supabase = await getSupabaseClient();
    const config = await getSupabaseConfig();
    const API_BASE_URL = await getApiBaseUrl();
    
    if (!supabase) {
      throw new Error('Backend service not available. Running in offline mode.');
    }
    
    console.log('🔐 ===== ATTEMPTING SUPABASE LOGIN =====');
    console.log('📧 Email:', email);
    console.log('🔐 Password length:', password.length);
    console.log('🔐 Password (first 3 chars):', password.substring(0, 3) + '...');
    console.log('🔐 Password (last 3 chars):', '...' + password.substring(password.length - 3));
    console.log('🔐 Password type:', typeof password);
    console.log('🔐 Has leading/trailing spaces?', password !== password.trim());
    console.log('🔐 Password trimmed length:', password.trim().length);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password,
      });

      if (error) {
        console.error('❌ Supabase login failed!');
        console.error('❌ Error code:', error.code);
        console.error('❌ Error name:', error.name);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error status:', error.status);
        console.error('❌ Full error object:', JSON.stringify(error, null, 2));
        
        // Enhanced error with context for better user feedback
        const enhancedError: any = new Error(error.message);
        enhancedError.code = error.code;
        enhancedError.status = error.status;
        enhancedError.name = error.name;
        enhancedError.originalError = error;
        
        throw enhancedError;
      }

      console.log('✅ Login successful!');
      console.log('✅ User ID:', data.user?.id);
      console.log('✅ User email:', data.user?.email);
      console.log('✅ Email confirmed:', data.user?.email_confirmed_at ? 'YES' : 'NO');
      console.log('✅ Session expires at:', data.session?.expires_at ? new Date(data.session.expires_at * 1000).toISOString() : 'unknown');
      console.log('✅ Access token length:', data.session?.access_token?.length || 0);
      
      // Verify session is persisted in localStorage
      const storedAuthToken = localStorage.getItem('sentlo-auth-token');
      console.log('✅ Session persisted to localStorage (sentlo-auth-token):', !!storedAuthToken);
      if (storedAuthToken) {
        try {
          const parsedToken = JSON.parse(storedAuthToken);
          console.log('✅ Stored session user:', parsedToken?.currentSession?.user?.email || 'unknown');
        } catch (e) {
          console.log('⚠️ Could not parse stored auth token');
        }
      }

      // Get user profile from backend
      const profile = await this.getUserProfile(data.user.id);

      // Store session info in separate key for backwards compatibility
      localStorage.setItem('sentlo_session', JSON.stringify({
        user: data.user,
        session: data.session,
        profile,
        timestamp: Date.now()
      }));

      return { user: data.user, profile };
    } catch (error: any) {
      // If it's a network error or Supabase is unreachable
      if (error.message && (
        error.message.includes('fetch') || 
        error.message.includes('network') ||
        error.message.includes('Failed to fetch')
      )) {
        console.error('❌ Network error - Supabase may be unreachable');
        const networkError: any = new Error('Unable to connect to authentication service. Please check your internet connection and try again.');
        networkError.code = 'network_error';
        throw networkError;
      }
      
      throw error;
    }
  },

  /**
   * Sign out current user
   */
  async signOut() {
    const supabase = await getSupabaseClient();
    if (supabase) {
      await supabase.auth.signOut();
    }
    localStorage.removeItem('sentlo_session');
    localStorage.removeItem('sentlo_user');
  },

  /**
   * Get current session
   */
  async getSession() {
    const supabase = await getSupabaseClient();
    
    if (!supabase) {
      // Check localStorage as fallback when Supabase is not available
      const stored = localStorage.getItem('sentlo_session');
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch {
          return null;
        }
      }
      return null;
    }
    
    const { data, error } = await supabase.auth.getSession();
    
    if (error || !data.session) {
      // Check localStorage as fallback
      const stored = localStorage.getItem('sentlo_session');
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch {
          return null;
        }
      }
      return null;
    }

    return data.session;
  },

  /**
   * Get user profile from backend
   */
  async getUserProfile(userId: string) {
    try {
      const API_BASE_URL = await getApiBaseUrl();
      const config = await getSupabaseConfig();
      
      if (!API_BASE_URL || !config) {
        console.log('📡 No API config available for profile fetch');
        return null;
      }
      
      const response = await fetch(`${API_BASE_URL}/user/profile/${userId}`, {
        headers: {
          'Authorization': `Bearer ${config.publicAnonKey}`,
        },
      });

      if (!response.ok) {
        console.log('📡 User profile not found (expected for new users)');
        return null;
      }

      const data = await response.json();
      return data.profile;
    } catch (error: any) {
      // Silently handle network errors - expected when backend unavailable or user not logged in
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.message?.includes('network')) {
        console.log('📡 Backend unavailable for profile fetch - working offline');
      } else {
        console.error('❌ Profile fetch error:', error);
      }
      return null;
    }
  },

  /**
   * Restore user session on app load
   */
  async restoreSession() {
    const session = await this.getSession();
    
    if (!session) {
      return null;
    }

    // If we have a valid session, get the latest profile
    if (session.user?.id) {
      const profile = await this.getUserProfile(session.user.id);
      return {
        user: session.user,
        profile: profile || session.profile
      };
    }

    return session;
  },

  /**
   * Get current user's profile from backend
   */
  async getProfile() {
    try {
      const API_BASE_URL = await getApiBaseUrl();
      const config = await getSupabaseConfig();
      const supabase = await getSupabaseClient();
      
      if (!API_BASE_URL || !config || !supabase) {
        // Try to get from localStorage as fallback
        const stored = localStorage.getItem('sentlo_session');
        if (stored) {
          try {
            const session = JSON.parse(stored);
            return { profile: session.profile };
          } catch {
            return { profile: null };
          }
        }
        return { profile: null };
      }

      // Get the current session to get access token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('No active session found');
        return { profile: null };
      }

      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error(`Profile fetch failed with status: ${response.status}`);
        return { profile: null };
      }

      const data = await response.json();
      return { profile: data.profile };
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      return { profile: null };
    }
  },

  /**
   * Alias for signUp (backwards compatibility)
   * Enhanced version that accepts an object and passes all parameters
   */
  async signup(data: { email: string; password: string; name: string; phone: string; country: string; state?: string; registrationSpeed?: number }) {
    try {
      console.log('📝 Signup called with data:', { 
        email: data.email, 
        name: data.name, 
        phone: data.phone, 
        country: data.country 
      });
      const result = await this.signUp(data.email, data.password, data.name, data.phone, data.country, data.state);
      console.log('✅ Signup successful');
      return result;
    } catch (error: any) {
      console.error('❌ Signup error in authService.signup:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      throw error;
    }
  }
};

// Helper to get auth headers with access token
const getAuthHeaders = async () => {
  const supabase = await getSupabaseClient();
  const config = await getSupabaseConfig();
  
  if (!supabase || !config) {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${config?.publicAnonKey || ''}`
    };
  }
  
  const { data: { session } } = await supabase.auth.getSession();
  
  // Use X-Supabase-Token header to bypass Edge Function JWT validation
  // This prevents "Invalid JWT" errors when tokens are expired
  if (session?.access_token) {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${config.publicAnonKey}`,
      'X-Supabase-Token': session.access_token
    };
  }
  
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${config.publicAnonKey}`
  };
};

// REPORTS SERVICE
export const reportsService = {
  async createReport(reportData: any) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/reports`, {
      method: 'POST',
      headers,
      body: JSON.stringify(reportData),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to create report');
    }
    
    return response.json();
  },
  
  async getReportsByCountry(country: string, type?: string) {
    const API_BASE_URL = await getApiBaseUrl();
    
    if (!API_BASE_URL) {
      console.log('⚠️ Backend not available, returning empty reports');
      return { reports: [] };
    }
    
    const url = type 
      ? `${API_BASE_URL}/reports/${country}?type=${type}`
      : `${API_BASE_URL}/reports/${country}`;
    
    try {
      console.log(`📡 Fetching reports from: ${url}`);
      
      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Backend error (${response.status}):`, errorText);
        
        // Return empty array instead of throwing - allow app to work
        return { reports: [] };
      }
      
      const data = await response.json();
      console.log(`✅ Loaded ${data.reports?.length || 0} reports from backend`);
      return data;
      
    } catch (error) {
      console.error('❌ Network error fetching reports:', error);
      // Return empty array on network errors - allow app to work offline
      return { reports: [] };
    }
  },
  
  async upvoteReport(reportId: string) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/reports/${reportId}/upvote`, {
      method: 'POST',
      headers,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to upvote report');
    }
    
    return response.json();
  },
  
  async getMyReports() {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/reports/user/me`, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to fetch your reports');
    }
    
    return response.json();
  },
  
  async getUserReports(userName: string) {
    const API_BASE_URL = await getApiBaseUrl();
    const config = await getSupabaseConfig();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    // Use public anon key for this public endpoint
    // This prevents Edge Function JWT validation errors
    const response = await fetch(`${API_BASE_URL}/reports/user/${encodeURIComponent(userName)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${config?.publicAnonKey}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to fetch user reports');
    }
    
    return response.json();
  },
  
  async updateReport(reportId: string, updateData: any) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/reports/${reportId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updateData),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to update report');
    }
    
    return response.json();
  },
  
  async deleteReport(reportId: string) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/reports/${reportId}`, {
      method: 'DELETE',
      headers,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to delete report');
    }
    
    return response.json();
  },

  async createRepost(postId: string, note?: string) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/reports/${postId}/repost`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ note }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to create repost');
    }
    
    return response.json();
  },

  async addComment(postId: string, text: string) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/reports/${postId}/comment`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ text }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to add comment');
    }
    
    return response.json();
  }
};

// WALLET SERVICE
export const walletService = {
  async getBalance() {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/wallet/balance`, {
      headers,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to fetch balance');
    }
    
    return response.json();
  },
  
  async redeemCoins(amount: number, redeemType: 'airtime' | 'electricity', phoneNumber: string) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/wallet/redeem`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ amount, redeemType, phoneNumber }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to redeem coins');
    }
    
    return response.json();
  }
};

// AJO SERVICE
export const ajoService = {
  async createCircle(circleData: any) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/ajo/create`, {
      method: 'POST',
      headers,
      body: JSON.stringify(circleData),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to create circle');
    }
    
    return response.json();
  },
  
  async getCircles(country: string) {
    const API_BASE_URL = await getApiBaseUrl();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/ajo/${country}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch circles');
    }
    
    return response.json();
  },
  
  async joinCircle(circleId: string) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/ajo/${circleId}/join`, {
      method: 'POST',
      headers,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to join circle');
    }
    
    return response.json();
  }
};

// MARKET SERVICE
export const marketService = {
  async submitPrice(priceData: any) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/market/prices`, {
      method: 'POST',
      headers,
      body: JSON.stringify(priceData),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to submit price');
    }
    
    return response.json();
  },
  
  async getPrices(country: string, city: string, item: string) {
    const API_BASE_URL = await getApiBaseUrl();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/market/prices/${country}/${city}/${item}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch prices');
    }
    
    return response.json();
  }
};

// MARKETPLACE SERVICE
export const marketplaceService = {
  async createListing(listingData: any) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/marketplace/listings`, {
      method: 'POST',
      headers,
      body: JSON.stringify(listingData),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to create listing');
    }
    
    return response.json();
  },
  
  async getListings(country: string, category?: string) {
    const API_BASE_URL = await getApiBaseUrl();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const url = category 
      ? `${API_BASE_URL}/marketplace/${country}?category=${category}`
      : `${API_BASE_URL}/marketplace/${country}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch listings');
    }
    
    return response.json();
  }
};

// ESCROW SERVICE
export const escrowService = {
  async createEscrow(escrowData: any) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/escrow/create`, {
      method: 'POST',
      headers,
      body: JSON.stringify(escrowData),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to create escrow');
    }
    
    return response.json();
  },
  
  async releaseEscrow(escrowId: string) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/escrow/${escrowId}/release`, {
      method: 'POST',
      headers,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to release escrow');
    }
    
    return response.json();
  }
};

// ALERTS SERVICE
export const alertsService = {
  async createAlert(alertData: any) {
    const API_BASE_URL = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/alerts`, {
      method: 'POST',
      headers,
      body: JSON.stringify(alertData),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Failed to create alert');
    }
    
    return response.json();
  },
  
  async getAlerts(country: string) {
    const API_BASE_URL = await getApiBaseUrl();
    
    if (!API_BASE_URL) {
      throw new Error('Backend service not available');
    }
    
    const response = await fetch(`${API_BASE_URL}/alerts/${country}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch alerts');
    }
    
    return response.json();
  }
};